/**
 * THE CABINET'S SHARED PARTS, AS THE PROTOTYPE DRAWS THEM (design 15, the owner's "adapt
 * UI/styles/layouts to the prototype", 2026-10-01). Every screen is rebuilt from these, so a tile, a
 * row of choices or a list card looks the same wherever it appears:
 *
 *   SectionLabel  the small gold-less capitals over a block - "NEEDS YOUR ATTENTION".
 *   StatTile      a figure in a card: a plain label, the number, an optional change against the
 *                 period before (an arrow and "3%", ./change-note.tsx), and one line saying what it
 *                 means.
 *   PillChoice    a row of pills of which one is chosen - a range - or, with `multiple`, any
 *                 number - a status filter. Each is a button that says whether it is pressed
 *                 (`aria-pressed`), so a screen reader hears the choice, and the chosen one is
 *                 the filled button's gold.
 *   ActionList    the Overview's rows: a coloured dot, what is waiting, a line under it, and the one
 *     ActionRow   thing to do about it at the right. The words are the row's link; the button is
 *                 the same destination for the eye, so it is hidden from the accessibility tree
 *                 rather than read out twice.
 *   ItemCard      a list entry as a card: a picture or its placeholder, small capitals over the
 *                 title (status, category), the title and standfirst, a note in gold, a line of
 *                 facts, and the entry's actions at the right.
 *   SectionCard   one section of an account page: a header strip with a round mark, the title
 *                 and who sees it, then the body - the profile, /settings and the notification
 *                 preferences.
 *
 * WHAT THEY DO NOT DO: decide any wording, fetch anything, or know any route. A screen hands them
 * words, numbers and nodes. Their measurements and colours are in ./kit.module.css.
 */
import { SimpleGrid } from '@mantine/core';
import type { LucideIcon } from '@moonx/ui';
import { useId } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import { ChangeNote } from './change-note';
import classes from './kit.module.css';

/* ------------------------------------------------------------------------- section label */

export interface SectionLabelProps {
  readonly children: ReactNode;
  /** The heading level a screen reader hears. The look is the same at every level. */
  readonly order?: 2 | 3;
  readonly id?: string;
}

export function SectionLabel({ children, order = 2, id }: SectionLabelProps) {
  const Heading = order === 2 ? 'h2' : 'h3';
  return (
    <Heading className={classes.sectionLabel} id={id}>
      {children}
    </Heading>
  );
}

/* ------------------------------------------------------------------------------ stat tile */

export interface StatDelta {
  /** Already worded, with no sign - the arrow says which way: "3%", "6 fewer", "no change", "3 pp". */
  readonly text: string;
  readonly tone: 'up' | 'down' | 'flat';
  /** The words a screen reader hears instead of the bare sign: "3% more than the 30 days before". */
  readonly says?: string;
}

export interface StatTileProps {
  readonly label: string;
  /** The figure, already formatted. A missing figure is a quiet word, never a fake zero. */
  readonly value: ReactNode;
  /** True when `value` is words ("not measured yet") rather than a number. */
  readonly quiet?: boolean;
  readonly delta?: StatDelta | undefined;
  /** One line: what the number means. Not a repeat of the label. */
  readonly tip?: ReactNode;
}

export function StatTile({ label, value, quiet = false, delta, tip }: StatTileProps) {
  return (
    <section className={classes.tile} aria-label={label}>
      <div className={classes.tileLabel}>{label}</div>
      <div className={classes.tileFigure}>
        <span className={quiet ? classes.tileValueQuiet : classes.tileValue}>{value}</span>
        {/* The change, quietly: a coloured arrow and plain words (design 18, ./change-note.tsx). */}
        {delta === undefined ? null : <ChangeNote change={delta} />}
      </div>
      {tip === undefined ? null : <div className={classes.tileTip}>{tip}</div>}
    </section>
  );
}

/* ---------------------------------------------------------------------------- pill choice */

export interface PillOption<T extends string> {
  readonly value: T;
  readonly label: string;
  /** A count beside the words - "Drafts 7". Omitted, none is drawn. */
  readonly count?: number | undefined;
}

interface PillChoiceBase<T extends string> {
  /** What the row chooses, for a screen reader: "Period", "Show". */
  readonly label: string;
  readonly options: readonly PillOption<T>[];
}

/** Exactly one pill is pressed: a range. */
interface PillChoiceOne<T extends string> extends PillChoiceBase<T> {
  readonly multiple?: false;
  readonly value: T;
  readonly onChange: (value: T) => void;
}

/**
 * Any number pressed at once: a filter of several states. A press toggles that pill. `clear` draws
 * a first pill that is pressed while nothing is chosen and empties the choice when pressed - the
 * articles list's "All".
 */
interface PillChoiceSeveral<T extends string> extends PillChoiceBase<T> {
  readonly multiple: true;
  readonly value: readonly T[];
  readonly onToggle: (value: T) => void;
  readonly clear?: { readonly label: string; readonly onClear: () => void } | undefined;
}

export type PillChoiceProps<T extends string> = PillChoiceOne<T> | PillChoiceSeveral<T>;

export function PillChoice<T extends string>(props: PillChoiceProps<T>) {
  const { label, options } = props;
  const pressed = (value: T): boolean =>
    props.multiple === true ? props.value.includes(value) : value === props.value;
  const press = (value: T): void => {
    if (props.multiple === true) {
      props.onToggle(value);
    } else {
      props.onChange(value);
    }
  };
  return (
    <div className={classes.pills} role="group" aria-label={label}>
      {props.multiple === true && props.clear !== undefined ? (
        <button
          type="button"
          className={classes.pill}
          aria-pressed={props.value.length === 0}
          onClick={props.clear.onClear}
        >
          {props.clear.label}
        </button>
      ) : null}
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={classes.pill}
          aria-pressed={pressed(option.value)}
          onClick={() => {
            press(option.value);
          }}
        >
          {option.label}
          {option.count === undefined ? null : (
            <span className={classes.pillCount}>{option.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------- action list */

export function ActionList({ children }: { readonly children: ReactNode }) {
  return <ul className={classes.actions}>{children}</ul>;
}

export interface ActionRowProps {
  /** The dot: something to do (red), something to know (blue), whenever you like (gold). */
  readonly tone: 'action' | 'info' | 'calm';
  /** What is waiting - usually the row's link. */
  readonly title: ReactNode;
  readonly status?: ReactNode;
  /** The visible button at the right; it repeats the title's destination for the eye only. */
  readonly action?: ReactNode;
}

export function ActionRow({ tone, title, status, action }: ActionRowProps) {
  const dot =
    tone === 'action' ? classes.dotAction : tone === 'info' ? classes.dotInfo : classes.dotCalm;
  return (
    <li className={classes.actionRow}>
      <span className={`${classes.dot} ${dot}`} aria-hidden="true" />
      <div className={classes.actionText}>
        <div className={classes.actionTitle}>{title}</div>
        {status === undefined ? null : <div className={classes.actionStatus}>{status}</div>}
      </div>
      {action === undefined ? null : <div aria-hidden="true">{action}</div>}
    </li>
  );
}

/* ------------------------------------------------------------------------------ item card */

export interface ItemCardProps {
  /** A picture, or `undefined` for the placeholder ("No cover"). */
  readonly media?: ReactNode;
  /** Words for the placeholder when there is no picture. */
  readonly mediaPlaceholder?: string;
  /** Small capitals over the title: a status pill, a category. */
  readonly eyebrow?: ReactNode;
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  /** A line in gold: "Editor left 2 comments", "Goes live 10 Aug, 20:00". */
  readonly note?: ReactNode;
  /** The line of facts under a rule: edited when, words, views, likes. */
  readonly meta?: ReactNode;
  /** The entry's buttons, stacked at the right (a row on a phone). */
  readonly actions?: ReactNode;
  /** The card's accessible name, when it is a landmark-worthy region. Usually the title's words. */
  readonly label?: string;
}

export function ItemCard({
  media,
  mediaPlaceholder,
  eyebrow,
  title,
  subtitle,
  note,
  meta,
  actions,
  label,
}: ItemCardProps) {
  const { t } = useTranslation('cabinet');
  return (
    <article className={classes.item} aria-label={label}>
      <div className={classes.itemMedia} aria-hidden={media === undefined ? 'true' : undefined}>
        {media ?? mediaPlaceholder ?? t('kit.noCover')}
      </div>
      <div className={classes.itemBody}>
        {eyebrow === undefined ? null : <div className={classes.itemEyebrow}>{eyebrow}</div>}
        <div className={classes.itemTitle}>{title}</div>
        {subtitle === undefined ? null : <div className={classes.itemSubtitle}>{subtitle}</div>}
        {note === undefined ? null : <div className={classes.itemNote}>{note}</div>}
        {meta === undefined ? null : <div className={classes.itemMeta}>{meta}</div>}
      </div>
      {actions === undefined ? null : <div className={classes.itemActions}>{actions}</div>}
    </article>
  );
}

/** One fact in an ItemCard's meta line, with an optional icon before it. */
export function MetaPart({
  icon,
  children,
}: {
  readonly icon?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <span className={classes.itemMetaPart}>
      {icon}
      {children}
    </span>
  );
}

/**
 * A row of tiles: `columns` across when the ROW has the room (four by default), two when it has
 * not. The room is the row's own width - a container query, not the window's - because the
 * cabinet's column is the window less the sidebar card: at a window of 1000 pixels the old
 * window-wide `md` put five Statistics tiles in about 130 pixels each, the figure and its change
 * crammed into two lines. A tile is kept at roughly 150 pixels or more; five that do not fit go
 * three and two.
 */
const TILE_COLUMNS: Readonly<Record<2 | 3 | 4 | 5, Readonly<Record<string, number>>>> = {
  2: { base: 2 },
  3: { base: 2, '480px': 3 },
  4: { base: 2, '620px': 4 },
  5: { base: 2, '480px': 3, '800px': 5 },
};

export function TileRow({
  children,
  columns = 4,
}: {
  readonly children: ReactNode;
  readonly columns?: 2 | 3 | 4 | 5;
}) {
  return (
    <SimpleGrid type="container" cols={TILE_COLUMNS[columns]} spacing={12} verticalSpacing={12}>
      {children}
    </SimpleGrid>
  );
}

/* --------------------------------------------------------------------------- section card */

export interface SectionCardProps {
  readonly title: string;
  /** In the small circle, as decoration beside the title: an icon... */
  readonly icon?: LucideIcon | undefined;
  /** ...or what the profile puts there: a section's number, or a drawn icon. */
  readonly mark?: ReactNode;
  /** The small word at the right of the title: who sees this. `public` is drawn in gold. */
  readonly tag?: { readonly words: string; readonly tone: 'public' | 'quiet' } | undefined;
  /** `danger`: the mark in the danger colour, for closing the account. */
  readonly tone?: 'calm' | 'danger';
  /** The section's own id - a link or a checklist jump lands on it - and its heading's. */
  readonly id?: string | undefined;
  readonly headingId?: string | undefined;
  /** A section a jump moves focus to: focusable by script only (`tabIndex={-1}`). */
  readonly focusTarget?: boolean;
  readonly children: ReactNode;
}

/**
 * ONE SECTION OF AN ACCOUNT PAGE, AS THE PROTOTYPE DRAWS ITS PROFILE'S SECTIONS (design 15): a card
 * on the surface whose header strip carries a round mark, the section's `<h2>`, and an optional
 * small word at the right - "Public", "Private" - then the section's body. The profile's numbered
 * sections (`features/application/components/FormSection.tsx`), /settings and the notification
 * preferences all draw it, so the three pages are one shape. Until 2026-10-05 the profile and
 * /settings each drew their own copy, a few pixels and a gold tint apart.
 *
 * THE SECTION IS NAMED BY ITS HEADING (`aria-labelledby`), so a screen reader's landmark list reads
 * "Delete my author account" and not "region". The mark is decoration beside the words.
 */
export function SectionCard({
  title,
  icon: Icon,
  mark,
  tag,
  tone = 'calm',
  id,
  headingId,
  focusTarget = false,
  children,
}: SectionCardProps) {
  const ownHeadingId = useId();
  const labelledBy = headingId ?? ownHeadingId;
  return (
    <section
      className={classes.sectionCard}
      id={id}
      aria-labelledby={labelledBy}
      tabIndex={focusTarget ? -1 : undefined}
    >
      <div className={classes.sectionHead}>
        <span
          className={`${classes.sectionMark} ${tone === 'danger' ? classes.sectionMarkDanger : ''}`}
          aria-hidden="true"
        >
          {Icon === undefined ? mark : <Icon size={13} aria-hidden />}
        </span>
        <h2 className={classes.sectionTitle} id={labelledBy}>
          {title}
        </h2>
        {tag === undefined ? null : (
          <span
            className={`${classes.sectionTag} ${
              tag.tone === 'public' ? classes.sectionTagPublic : classes.sectionTagQuiet
            }`}
          >
            {tag.words}
          </span>
        )}
      </div>
      <div className={classes.sectionBody}>{children}</div>
    </section>
  );
}

/** A section card's small inset note - the signs line, a quiet status - on a step above the card. */
export function SectionInset({ children }: { readonly children: ReactNode }) {
  return <div className={classes.sectionInset}>{children}</div>;
}
